import React, { useCallback } from "react";

import { Chip, Stack, TablePagination, Typography } from "@mui/material";
import MuiTable from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { TableCellProps } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import { IContact, IContactsPage } from "store/types/user-management";

import { contactName, formatAddDate } from "../helpers";

const cellTextStyles: TableCellProps["sx"] = {
  whiteSpace: "nowrap",
  textOverflow: "ellipsis",
  overflow: "hidden",
  padding: "0 20px",
  color: "#0a3142",
  fontSize: "16px",
  height: "50px"
};

const Muted: React.FC = () => (
  <Typography component="span" sx={{ color: "#898989" }}>
    {"—"}
  </Typography>
);

interface HeadCell {
  key: string;
  title: string;
}

const headCells: HeadCell[] = [
  { key: "person", title: "Person" },
  { key: "email", title: "Email" },
  { key: "title", title: "Title" },
  { key: "skill", title: "Skill" },
  { key: "asset_class", title: "Asset Class" },
  { key: "organization", title: "Organization" },
  { key: "add_date", title: "Add Date" },
  { key: "deal", title: "Deal" }
];

interface Props {
  data: IContactsPage;
  page: number;
  rowsPerPage: number;
  handleChangePage: (newPage: number) => void;
  // Optional — the Person link opens a contact when a handler is provided (detail/edit screen TBD).
  onSelectContact?: (contact: IContact) => void;
}

// NOTE: the leading select-checkbox column (header + per-row) is intentionally omitted for now — there
// are no bulk actions to apply to a selection yet. Re-add it once a bulk-action bar exists.
export const Table: React.FC<Props> = ({ data, page, rowsPerPage, handleChangePage, onSelectContact }) => {
  const { total, data: rowsData } = data;

  const renderDealChips = useCallback((contact: IContact) => {
    const [firstDeal, ...rest] = contact.deals;
    if (!firstDeal) return <Muted />;

    const chipSx = {
      height: "24px",
      borderRadius: "20px",
      backgroundColor: "#f6f8fc",
      color: "#071f35",
      fontSize: "12px"
    } as const;

    return (
      <Stack direction="row" sx={{ gap: "4px", alignItems: "center" }}>
        <Chip label={firstDeal.name} sx={chipSx} />
        {rest.length > 0 && <Chip label={`+${rest.length}`} sx={chipSx} />}
      </Stack>
    );
  }, []);

  return (
    <Stack sx={{ flexGrow: 1, overflow: "hidden", width: "100%" }}>
      <TableContainer
        sx={{
          width: "100%",
          borderRadius: "10px",
          border: "1px solid #dae0e2",
          borderTop: "none",
          paddingBottom: "7px",
          flexGrow: 1,
          backgroundColor: "white"
        }}
      >
        <MuiTable sx={{ width: "100%", color: "#0a3142" }} aria-labelledby="contactsTable">
          <TableHead sx={{ position: "sticky", top: 0, backgroundColor: "white", height: "40px" }}>
            <TableRow sx={{ backgroundColor: "#edf1f9" }}>
              {headCells.map(({ title, key }) => (
                <TableCell sx={{ fontSize: "15px", fontWeight: 500, ...cellTextStyles }} key={key}>
                  {title}
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {rowsData.map((row) => {
              const name = contactName(row);
              return (
                <TableRow key={row.id} hover sx={{ ":hover": { backgroundColor: "#f5f7f7" } }}>
                  <TableCell
                    align="left"
                    onClick={onSelectContact ? () => onSelectContact(row) : undefined}
                    sx={{
                      width: "180px",
                      maxWidth: "180px",
                      ...cellTextStyles,
                      color: "#26A6AE",
                      cursor: onSelectContact ? "pointer" : "default"
                    }}
                  >
                    {name || <Muted />}
                  </TableCell>
                  <TableCell align="left" sx={{ width: "180px", maxWidth: "180px", ...cellTextStyles }}>
                    {row.email || <Muted />}
                  </TableCell>
                  <TableCell align="left" sx={{ width: "140px", maxWidth: "140px", ...cellTextStyles }}>
                    {row.title || <Muted />}
                  </TableCell>
                  <TableCell align="left" sx={{ width: "140px", maxWidth: "140px", ...cellTextStyles }}>
                    {row.additional_information?.skill || <Muted />}
                  </TableCell>
                  <TableCell align="left" sx={{ width: "140px", maxWidth: "140px", ...cellTextStyles }}>
                    {row.additional_information?.asset_class || <Muted />}
                  </TableCell>
                  <TableCell align="left" sx={{ width: "160px", maxWidth: "160px", ...cellTextStyles }}>
                    {row.firm || <Muted />}
                  </TableCell>
                  <TableCell align="left" sx={{ width: "120px", maxWidth: "120px", ...cellTextStyles }}>
                    {formatAddDate(row.created_at) || <Muted />}
                  </TableCell>
                  <TableCell align="left" sx={{ width: "160px", maxWidth: "160px", ...cellTextStyles }}>
                    {renderDealChips(row)}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </MuiTable>
      </TableContainer>
      <TablePagination
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          flexShrink: 0,
          height: "40px",
          ".MuiTablePagination-toolbar": { minHeight: "40px" }
        }}
        rowsPerPageOptions={[rowsPerPage]}
        component="div"
        rowsPerPage={rowsPerPage}
        count={total}
        page={page - 1}
        onPageChange={(_, newPage) => handleChangePage(newPage)}
      />
    </Stack>
  );
};
